import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Plus, Search } from "lucide-react";
import { useMemo } from "react";
import { CanProject } from "@/app/capabilities/components/can-project";
import { PROJECT } from "@/app/capabilities/permissions";
import { ListLayout } from "@/components/custom/list-layout/list-layout";
import { EmptyResourceState } from "@/components/custom/resources/empty-resource-state";
import {
  ResourceContainer,
  type ResourceSection,
} from "@/components/custom/resources/resource-container";
import { ResourceViewSwitcher } from "@/components/custom/resources/resource-view-switcher";
import { useGetOAuthProviders } from "../api/hooks";
import type { OAuthProviderItem } from "../types";
import { OAuthSheet } from "./components/credentials/oauth-sheet";
import { useOAuthViewOptions } from "./components/oauth-view-options";
import { OAuthTable } from "./components/table/oauth-table";

export const OAuthList = () => {
  const { name: searchTerm = "" } = useSearch({
    from: "/_authenticated/orgs/$organizationId/projects/$projectId/auth/oauth/",
  });
  const navigate = useNavigate();

  // Global column visibility state for all oauth tables
  const {
    columnVisibility,
    setColumnVisibility,
    component: ViewOptionsComponent,
  } = useOAuthViewOptions();

  const { data: allProviders } = useGetOAuthProviders("project");

  const { projectProviders, tenantProviders, staticProviders } = useMemo(() => {
    if (!allProviders?.length) {
      return { projectProviders: [], tenantProviders: [], staticProviders: [] };
    }

    const normalizedSearchTerm = (searchTerm ?? "").toLowerCase().trim();

    const filterBySearch = (item: OAuthProviderItem) => {
      if (!normalizedSearchTerm) {
        return true;
      }
      return (
        (item.id ?? "").toLowerCase().includes(normalizedSearchTerm) ||
        (item.provider_id ?? "").toLowerCase().includes(normalizedSearchTerm) ||
        (item.description ?? "").toLowerCase().includes(normalizedSearchTerm)
      );
    };

    const filteredProviders = allProviders.filter(filterBySearch);

    return {
      projectProviders: filteredProviders.filter(
        (provider) => provider.binding.type === "project"
      ),
      tenantProviders: filteredProviders.filter(
        (provider) => provider.binding.type === "tenant"
      ),
      staticProviders: filteredProviders.filter(
        (provider) => provider.binding.type === "static"
      ),
    };
  }, [allProviders, searchTerm]);

  // Override logic: project > tenant > static
  const {
    overriddenTenantProviders,
    overriddenStaticProviders,
    overridingProjectProviders,
    overridingTenantProviders,
  } = useMemo(() => {
    // Use provider_id for override logic so overrides are determined by the same provider across bindings
    const projectProviderIds = new Set(
      projectProviders?.map((p) => p.provider_id) || []
    );
    const tenantProviderIds = new Set(
      tenantProviders?.map((p) => p.provider_id) || []
    );
    const staticProviderIds = new Set(
      staticProviders?.map((p) => p.provider_id) || []
    );

    return {
      // Tenant providers overridden by project providers
      overriddenTenantProviders: new Set(
        tenantProviders
          ?.filter((p) => projectProviderIds.has(p.provider_id))
          .map((p) => p.provider_id) || []
      ),
      // Static providers overridden by project OR tenant providers
      overriddenStaticProviders: new Set(
        staticProviders
          ?.filter(
            (p) =>
              projectProviderIds.has(p.provider_id) ||
              tenantProviderIds.has(p.provider_id)
          )
          .map((p) => p.provider_id) || []
      ),
      // Project providers that override tenant providers
      overridingProjectProviders: new Set(
        projectProviders
          ?.filter(
            (p) =>
              tenantProviderIds.has(p.provider_id) ||
              staticProviderIds.has(p.provider_id)
          )
          .map((p) => p.provider_id) || []
      ),
      // Tenant providers that override static providers (but not overridden by project)
      overridingTenantProviders: new Set(
        tenantProviders
          ?.filter(
            (p) =>
              staticProviders?.some(
                (staticProvider) => staticProvider.provider_id === p.provider_id
              ) && !projectProviderIds.has(p.provider_id)
          )
          .map((p) => p.provider_id) || []
      ),
    };
  }, [projectProviders, tenantProviders, staticProviders]);

  // Build resource sections using the new Resource components
  const sections: ResourceSection[] = useMemo(() => {
    const sectionsToShow: ResourceSection[] = [];
    let isFirstSectionWithData = true;

    if (projectProviders && projectProviders.length > 0) {
      sectionsToShow.push({
        binding: "project",
        itemsCount: projectProviders.length,
        resourceType: "OAuth",
        defaultOpen: isFirstSectionWithData,
        children: (
          <OAuthTable
            columnVisibility={columnVisibility}
            items={projectProviders}
            onColumnVisibilityChange={setColumnVisibility}
            overridingProviders={overridingProjectProviders}
          />
        ),
      });
      isFirstSectionWithData = false;
    }

    if (tenantProviders && tenantProviders.length > 0) {
      sectionsToShow.push({
        binding: "tenant",
        itemsCount: tenantProviders.length,
        resourceType: "OAuth",
        defaultOpen: isFirstSectionWithData,
        overriddenCount: overriddenTenantProviders.size,
        overriddenTooltipContent:
          "Some providers are overridden by project providers",
        children: (
          <OAuthTable
            columnVisibility={columnVisibility}
            items={tenantProviders}
            onColumnVisibilityChange={setColumnVisibility}
            overriddenProviders={overriddenTenantProviders}
            overridingProviders={overridingTenantProviders}
          />
        ),
      });
      isFirstSectionWithData = false;
    }

    if (staticProviders && staticProviders.length > 0) {
      sectionsToShow.push({
        binding: "static",
        itemsCount: staticProviders.length,
        resourceType: "OAuth",
        defaultOpen: isFirstSectionWithData,
        overriddenCount: overriddenStaticProviders.size,
        overriddenTooltipContent:
          "Some providers are overridden by higher-level providers",
        children: (
          <OAuthTable
            columnVisibility={columnVisibility}
            items={staticProviders}
            onColumnVisibilityChange={setColumnVisibility}
            overriddenProviders={overriddenStaticProviders}
          />
        ),
      });
      isFirstSectionWithData = false;
    }

    return sectionsToShow;
  }, [
    projectProviders,
    tenantProviders,
    staticProviders,
    columnVisibility,
    setColumnVisibility,
    overriddenTenantProviders,
    overridingTenantProviders,
    overridingProjectProviders,
    overriddenStaticProviders,
  ]);

  return (
    <ListLayout
      actions={
        <CanProject permission={PROJECT.authProviders.create}>
          <OAuthSheet
            trigger={
              <Button size="sm">
                <Plus className="mr-2 size-4" />
                Add OAuth Provider
              </Button>
            }
          />
        </CanProject>
      }
      description="Manage your OAuth providers for authentication."
      filters={
        <>
          <div className="flex items-center gap-3">
            <div className="relative max-w-md">
              <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="h-9 w-full pl-9"
                onChange={(event) => {
                  navigate({
                    to: ".",
                    search: (prev: Record<string, unknown>) => ({
                      ...prev,
                      name: event.target.value,
                    }),
                  });
                }}
                placeholder="Search OAuth providers..."
                value={searchTerm}
              />
            </div>
            {ViewOptionsComponent}
          </div>
          <ResourceViewSwitcher />
        </>
      }
      title="OAuth Providers"
    >
      {sections.length > 0 ? (
        <ResourceContainer
          defaultActiveTab={`${sections[0].binding}-${sections[0].resourceType}`}
          sections={sections}
        />
      ) : (
        <EmptyResourceState
          description={
            allProviders && allProviders.length > 0
              ? "No providers match your search criteria. Try adjusting your search terms."
              : "OAuth providers enable secure authentication and authorization with external services. Set up providers to allow users to connect their accounts."
          }
          resourceType={
            allProviders && allProviders.length > 0
              ? "matching oauth providers"
              : "oauth providers"
          }
        />
      )}
    </ListLayout>
  );
};
